import {
  currentIndexedDbFactory,
  isQuotaExceeded,
  openIndexedDb,
  runIndexedDbTransaction,
} from "%/lib/indexed-db";

export const DCE_CACHE_DATABASE = "dce-caches";
export const DCE_CACHE_DATABASE_VERSION = 1;
export const COMPILED_STORE = "compiled";
export const DEFAULTS_STORE = "defaults";
export const LEDGER_STORE = "ledger";
export const LEGACY_LOCAL_STORAGE_PREFIXES = ["dce:compiled:", "dce:defaults:"];
export const KEPT_PREVIOUS_DEFAULTS = 3;
export const KEPT_COMPILED_PAGES = 100;

const ALL_STORES = [COMPILED_STORE, DEFAULTS_STORE, LEDGER_STORE];

const purgedLegacyStorages = new WeakSet();
const sweepsByFactory = new WeakMap();
const viewerClearsByFactory = new WeakMap();
let legacyPurgeFailureWarned = false;
let activeDefaultsVersion = null;

export function rememberCurrentDefaultsVersion(version) {
  if (version) {
    activeDefaultsVersion = version;
  }
}

export function currentDefaultsVersion() {
  return activeDefaultsVersion ?? window.blclient?.data?.compiledDefaultsVersion ?? null;
}

function openCacheDb() {
  return openIndexedDb(DCE_CACHE_DATABASE, DCE_CACHE_DATABASE_VERSION, ALL_STORES);
}

function ledgerKey(store, key) {
  return `${store}/${key}`;
}

function describeError(error) {
  return `${error?.name ?? "Error"}: ${error?.message ?? error}`;
}

function warnCacheFailure(action, store, key, error, consequence) {
  console.warn( // eslint-disable-line no-console
    `[DCE] IndexedDB ${action} of ${DCE_CACHE_DATABASE}.${store} key ${key} failed with ${describeError(error)}; ${consequence}`,
    { store, key, error },
  );
}

function isLegacyKey(key) {
  return LEGACY_LOCAL_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix));
}

export function purgeLegacyLocalStorageCaches() {
  try {
    const storage = window.localStorage;
    if (!storage || purgedLegacyStorages.has(storage)) {
      return [];
    }
    purgedLegacyStorages.add(storage);
    const legacyKeys = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key !== null && isLegacyKey(key)) {
        legacyKeys.push(key);
      }
    }
    legacyKeys.forEach(key => storage.removeItem(key));
    return legacyKeys;
  } catch (error) {
    if (!legacyPurgeFailureWarned) {
      legacyPurgeFailureWarned = true;
      console.warn( // eslint-disable-line no-console
        `[DCE] the legacy ${LEGACY_LOCAL_STORAGE_PREFIXES.join(" and ")} localStorage keys could not be deleted `
        + `(${describeError(error)}); they stay until localStorage can be opened, and the DCE caches use IndexedDB either way`,
        { error },
      );
    }
    return [];
  }
}

async function readyCacheDb() {
  purgeLegacyLocalStorageCaches();
  const factory = currentIndexedDbFactory();
  const clearFailure = factory ? await viewerClearsByFactory.get(factory) : null;
  if (clearFailure) {
    throw new Error(
      `the previous viewer's cached pages could not be cleared (${describeError(clearFailure)}), so nothing is read `
      + "from or written to the DCE caches on this page load",
      { cause: clearFailure },
    );
  }
  return openCacheDb();
}

export async function readCacheEntry(store, key) {
  try {
    const db = await readyCacheDb();
    const entry = await runIndexedDbTransaction(db, [store], "readonly", stores => {
      const request = stores[store].get(key);
      return () => request.result;
    });
    return entry && typeof entry === "object" ? entry : null;
  } catch (error) {
    warnCacheFailure("read", store, key, error, "it is fetched from the network instead");
    return null;
  }
}

function putEntry(db, store, key, entry) {
  return runIndexedDbTransaction(db, [store, LEDGER_STORE], "readwrite", stores => {
    stores[store].put(entry, key);
    stores[LEDGER_STORE].put(
      { store, key, storedAt: entry.storedAt, expiresAt: entry.expiresAt ?? null },
      ledgerKey(store, key),
    );
  });
}

function clearStores(db, storeNames) {
  return runIndexedDbTransaction(db, storeNames, "readwrite", stores => {
    storeNames.forEach(storeName => stores[storeName].clear());
  });
}

async function retryAfterClearingCaches(db, store, key, entry, quotaError) {
  try {
    await clearStores(db, ALL_STORES);
    await putEntry(db, store, key, entry);
    console.warn( // eslint-disable-line no-console
      `[DCE] IndexedDB refused ${DCE_CACHE_DATABASE}.${store} key ${key} with ${describeError(quotaError)}, so every `
      + "cached DCE page and defaults bundle was cleared and the write retried once, which succeeded; "
      + "the origin's IndexedDB quota is nearly full",
      { store, key, error: quotaError },
    );
    return true;
  } catch (retryError) {
    warnCacheFailure(
      "write",
      store,
      key,
      retryError,
      `the first attempt failed with ${describeError(quotaError)}, every cached DCE entry was cleared and the retry `
      + "failed too; the page rendered from the network copy and the next load fetches it again",
    );
    return false;
  }
}

export async function writeCacheEntry(store, key, entry) {
  const stamped = { ...entry, storedAt: Date.now() };
  let db = null;
  try {
    db = await readyCacheDb();
    await putEntry(db, store, key, stamped);
    return true;
  } catch (error) {
    if (db && isQuotaExceeded(error)) {
      return retryAfterClearingCaches(db, store, key, stamped, error);
    }
    warnCacheFailure("write", store, key, error, "the page rendered from the network copy and the next load fetches it again");
    return false;
  }
}

export async function deleteCacheEntry(store, key) {
  try {
    const db = await readyCacheDb();
    await runIndexedDbTransaction(db, [store, LEDGER_STORE], "readwrite", stores => {
      stores[store].delete(key);
      stores[LEDGER_STORE].delete(ledgerKey(store, key));
    });
    return true;
  } catch (error) {
    warnCacheFailure("delete", store, key, error, "the stale entry stays until the next successful write or sweep");
    return false;
  }
}

function isExpired(row, now) {
  return !row.expiresAt || now >= row.expiresAt * 1000;
}

function newestFirst(rows) {
  return [...rows].sort((left, right) => (Number(right.storedAt) || 0) - (Number(left.storedAt) || 0));
}

function staleDefaults(rows, currentVersion) {
  const previous = rows.filter(row => row.store === DEFAULTS_STORE && row.key !== currentVersion);
  return newestFirst(previous).slice(KEPT_PREVIOUS_DEFAULTS);
}

function staleCompiledPages(rows, now) {
  const pages = rows.filter(row => row.store === COMPILED_STORE);
  const expired = pages.filter(row => isExpired(row, now));
  const live = pages.filter(row => !isExpired(row, now));
  return [...expired, ...newestFirst(live).slice(KEPT_COMPILED_PAGES)];
}

export async function sweepCompiledCaches({ currentDefaultsVersion: currentVersion = null, now = Date.now() } = {}) {
  const db = await readyCacheDb();
  const rows = await runIndexedDbTransaction(db, [LEDGER_STORE], "readonly", stores => {
    const request = stores[LEDGER_STORE].getAll();
    return () => request.result ?? [];
  });
  const doomed = [...staleDefaults(rows, currentVersion), ...staleCompiledPages(rows, now)];
  if (doomed.length > 0) {
    await runIndexedDbTransaction(db, ALL_STORES, "readwrite", stores => {
      doomed.forEach(row => {
        stores[row.store].delete(row.key);
        stores[LEDGER_STORE].delete(ledgerKey(row.store, row.key));
      });
    });
  }
  return { droppedKeys: doomed.map(row => ledgerKey(row.store, row.key)) };
}

export function sweepCompiledCachesOncePerPage({ currentDefaultsVersion: currentVersion = currentDefaultsVersion() } = {}) {
  purgeLegacyLocalStorageCaches();
  const factory = currentIndexedDbFactory();
  if (!factory) {
    return Promise.resolve({ droppedKeys: [] });
  }
  if (!sweepsByFactory.has(factory)) {
    sweepsByFactory.set(factory, sweepCompiledCaches({ currentDefaultsVersion: currentVersion }).catch(error => {
      console.warn( // eslint-disable-line no-console
        `[DCE] sweeping stale entries from IndexedDB ${DCE_CACHE_DATABASE} failed with ${describeError(error)}; `
        + "stale defaults and expired pages stay until the next page load sweeps again",
        { error },
      );
      return { droppedKeys: [] };
    }));
  }
  return sweepsByFactory.get(factory);
}

export function clearViewerScopedDceCaches() {
  const factory = currentIndexedDbFactory();
  if (!factory) {
    return Promise.resolve(null);
  }
  const clearing = (viewerClearsByFactory.get(factory) ?? Promise.resolve(null))
      .then(() => openCacheDb())
      .then(db => clearStores(db, ALL_STORES))
      .then(() => null, error => {
        console.warn( // eslint-disable-line no-console
          `[DCE] the viewer changed but IndexedDB ${DCE_CACHE_DATABASE} could not be cleared (${describeError(error)}); `
          + "no cached page is read on this page load",
          { error },
        );
        return error;
      });
  viewerClearsByFactory.set(factory, clearing);
  return clearing;
}
